/**
 * useQueryWithState — wrap a Convex query in an explicit
 * loading / error / success / retry state machine.
 *
 * Why this shape:
 *   Convex's stock `useQuery` returns `T | undefined` and *throws*
 *   on error. That's the wrong default for in-panel UX because a
 *   single transient failure unmounts the entire dashboard up to
 *   the nearest React error boundary, blanking working panels
 *   alongside the broken one. We instead build on top of
 *   `useQueries`, which exposes errors as values rather than
 *   throwing, then wrap the result in a discriminated union so
 *   every consumer is forced (by the type system) to handle each
 *   state explicitly. The retry escape hatch lives on the error
 *   variant where it is actually meaningful — TypeScript narrows
 *   `data` only inside the `success` arm, which kills the
 *   historical `data?.field` ternary pattern that hid loading
 *   conditions behind silent undefineds.
 *
 *   "Retry" is implemented by remounting the underlying
 *   `useQueries` subscription via a counter folded into the dict
 *   key. Convex deduplicates the actual server watch by
 *   (query, args), so this won't re-execute a deterministic-fail
 *   query — but for the much more common case of a transient
 *   network blip, an auth hiccup, or a brief backend stall, the
 *   re-subscribe gives the panel a clean transition back to
 *   `loading` and lets the next pushed result take effect. For a
 *   guaranteed reset the user still has the ErrorBoundary's
 *   "Reload page" link as the last-resort escape hatch.
 */
import { useQueries } from "convex/react";
import {
  getFunctionName,
  type FunctionArgs,
  type FunctionReference,
  type FunctionReturnType,
} from "convex/server";
import { useCallback, useMemo, useState } from "react";

export type QueryState<T> =
  | { status: "loading" }
  | { status: "error"; error: Error; retry: () => void }
  | { status: "success"; data: T };

const QUERY_KEY_PREFIX = "useQueryWithState";

export function useQueryWithState<Q extends FunctionReference<"query">>(
  query: Q,
  args: FunctionArgs<Q>,
): QueryState<FunctionReturnType<Q>> {
  // The retry counter is the single source of state; bumping it
  // produces a new dict key, which forces `useQueries` to drop
  // the old subscription and create a fresh one. We do NOT smuggle
  // the counter into `args` because Convex validates args server-
  // side and would reject the unknown field.
  const [retryCount, setRetryCount] = useState(0);
  const retry = useCallback(() => {
    setRetryCount((n) => n + 1);
  }, []);

  // CRITICAL: this dict MUST be referentially stable across
  // renders that have the same (query, args, retryCount). Convex's
  // `useQueries` internally builds a `useMemo([observer, queries])`
  // for the subscription object, and `useSubscription` calls
  // `setState` *during render* whenever its `getCurrentValue`/
  // `subscribe` references change. Passing a fresh `{[key]: …}`
  // object every render therefore triggers a setState-in-render
  // loop and React aborts with error #301 ("Too many re-renders").
  //
  // The challenge is that the `query` parameter is a Convex
  // `FunctionReference`, which in the generated `api` is a
  // `Proxy`-backed object that returns a NEW reference on every
  // property access. `api.queries.getStats` therefore yields a
  // fresh object each render of `App.tsx`, which would invalidate
  // any memo keyed on `query` referentially. The fix — mirroring
  // the technique used by Convex's own stock `useQuery` hook —
  // is to extract `getFunctionName(query)`, a STRING identifier
  // that is stable across proxy accesses, and use that string in
  // the memo deps. Combined with a JSON-stable `argsKey`, the
  // memo cell is now keyed on pure value identity, so referentially
  // unstable inputs collapse to the same cell as long as their
  // semantic identity is unchanged.
  const queryName = getFunctionName(query);
  const argsKey = useMemo(() => stableStringify(args), [args]);
  const key = `${QUERY_KEY_PREFIX}:${retryCount}`;

  // `useQueries` types `args` as `Record<string, Value>`, but the
  // generic `FunctionArgs<Q>` is structurally compatible at the
  // call site for any concrete query. The `as never` cast lets
  // TypeScript erase the friction without sacrificing the strong
  // arg typing on the public surface of this hook.
  const queries = useMemo(
    () => ({ [key]: { query, args: args as never } }),
    // `queryName` is the stable string id of `query`; `argsKey`
    // is the JSON-stable identity of `args`. Both `query` and
    // `args` themselves are intentionally omitted from the deps
    // because they may be referentially unstable (proxy-backed
    // FunctionReference, fresh object literals from the caller)
    // — including them would re-introduce the loop this memo
    // exists to break.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryName, argsKey, key],
  );
  const result = useQueries(queries);
  const value = result[key];

  if (value === undefined) {
    return { status: "loading" };
  }
  // Convex's `useQueries` documents `Error` instances as the
  // canonical failure shape — the real Error path is the primary
  // success criterion below. The defensive fallback exists ONLY
  // for an explicit opt-in escape hatch: a value carrying the
  // sentinel `__isError === true` field is treated as an error.
  // Earlier iterations of this hook also matched values that
  // happened to carry `name` / `code` / `stack` fields, but that
  // heuristic was too eager: legitimate domain objects (graded
  // signal rows have `code`, regime cells have `name`, error log
  // rows have `stack`) would be misclassified as failures (QST-214
  // review pass 3, Qodo finding 'False Positives'). The strict
  // sentinel-only fallback eliminates the false-positive class
  // while still letting future Convex error wrappers opt in.
  if (value instanceof Error) {
    return { status: "error", error: value, retry };
  }
  if (hasErrorSentinel(value)) {
    // Preserve fidelity when wrapping a sentinel-shaped value
    // into a real Error (QST-214 review pass 4, Qodo finding
    // 'Error Fidelity'): copy `name` if present and pass the
    // original sentinel through `cause` so telemetry sinks can
    // recover any caller-attached metadata. Stack frames cannot
    // be reconstructed from a structurally-shaped value, but
    // anything the caller chose to attach stays reachable.
    const wrapped = new Error(value.message, { cause: value });
    if (typeof value.name === "string" && value.name) {
      wrapped.name = value.name;
    }
    return { status: "error", error: wrapped, retry };
  }
  return {
    status: "success",
    data: value as FunctionReturnType<Q>,
  };
}

/**
 * Conservative predicate: a value is treated as an error if and
 * only if it carries the explicit `__isError === true` sentinel
 * AND a string `message` field. Anything that wants to use this
 * fallback path must opt in deliberately; we never infer error
 * shape from incidental field overlap with domain types.
 */
function hasErrorSentinel(
  value: unknown,
): value is { message: string; name?: string } {
  if (value === null || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  if (obj.__isError !== true) return false;
  return typeof obj.message === "string";
}

/**
 * Order-independent JSON serialization. Used as the memoization
 * key for the queries dict so that arg objects with identical
 * content but different key insertion order collapse to the same
 * cell. Convex args are JSON-serializable by contract
 * (validators reject anything else), so a recursive object-walk
 * is sufficient — we don't need to handle Maps, Sets, Dates, etc.
 *
 * Returning a string (not an object) lets us use the result
 * directly in a `useMemo` deps array without referential
 * instability — string equality is value equality.
 */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  const parts = keys.map(
    (k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`,
  );
  return `{${parts.join(",")}}`;
}
